import { useDynamicLocale } from '@/hoslog-intl';
import { cn } from '@/lib/utils';
import { useController, useFormContext } from 'react-hook-form';
import { Select as Root, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'ui/select';
import { useFormField } from '.';
import { FormField } from './Field';
import { UseFormFieldProps } from './type';

interface Props extends UseFormFieldProps, React.ComponentProps<typeof Root> {
  name: string;
  options: { label: string; value: string; labelEng?: string; disabled?: boolean }[];
  placeholder?: string;
  selected?: string;
  triggerClassName?: string;
  hideDefaultOption?: boolean;
}

function Select(props: Props) {
  const { formFieldProps, childProps, error } = useFormField(props);
  const { control, trigger } = useFormContext();
  const { locale, t } = useDynamicLocale();
  const { options, name, placeholder, selected, hideDefaultOption } = props;

  const { field } = useController({ name, control });

  return (
    <FormField {...formFieldProps} {...props}>
      <Root
        {...childProps}
        onValueChange={(val) => {
          field.onChange(val);
          void trigger(name);
        }}
        value={(field.value as string) || selected || 'default'}
      >
        <SelectTrigger id={name} className={cn(error ? 'border-destructive focus-visible:ring-destructive' : '')}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>

        <SelectContent>
          {!hideDefaultOption && (
            <SelectItem value="default">{placeholder ? placeholder : t('common.form.placeholder.select')}</SelectItem>
          )}
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
              {locale === 'en' && option.labelEng ? option.labelEng : option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Root>
    </FormField>
  );
}

Select.displayName = 'Select';
export default Select;
